import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuthentication } from '../hooks/useAuthentication';
import Notification from "./Notification";
import MobileNavbar from "./MobileNavbar";
import PageLoader from "./PageLoader";
import './Navbar.css';

import { useUserAPI } from '../contexts/UserAPIContext';
import { authFetch } from '../utils/authFetch';

const Navbar = () => {
    const { userAPIData, loading } = useUserAPI();
    const [terapeutaLogada, setTerapeutaLogada] = useState(null);
    const { logout } = useAuthentication();
    const navigate = useNavigate();
    const location = useLocation();
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const collapseRef = useRef(null);
    const [notificacoes, setNotificacoes] = useState([]);
    const [loadingNotificacoes, setLoadingNotificacoes] = useState(false);

    const isMobile = window.innerWidth < 992;

    // Função para verificar se o usuário tem acesso a um item do menu
    const hasAccess = (menuItem) => {
        if (!userAPIData) {
            return false;
        }

        // Tenta pegar o roleId de diferentes estruturas possíveis
        const roleId = userAPIData.userRole?.id || userAPIData.userRoleId;

        // Verificação explícita e defensiva
        if (roleId === null || roleId === undefined) {
            return false;
        }

        // Se roleId = 6, mostrar APENAS o item agendamentos (calendário somente leitura)
        // Verificação estrita com === para evitar problemas de tipo
        if (Number(roleId) === 6) {
            return menuItem === 'agendamentos';
        }

        // Se roleId = 4, mostrar apenas itens específicos
        if (Number(roleId) === 4) {
            const allowedItems = [
                'dashboard',
                'agendamentos',
                'nosso-time',
                'pacientes',
                'responsaveis',
                'prestadores-servico'
            ];
            return allowedItems.includes(menuItem);
        }

        // Se roleId = 5, mostrar apenas o item exportações
        if (Number(roleId) === 5) {
            return menuItem === 'exportacoes';
        }

        // Para outros roles, mostrar todos os itens (comportamento padrão)
        return true;
    };

    const buscarTerapeutaLogada = async () => {
        try {
            const response = await authFetch(`therapist`);
            const data = await response.json();
            
            const terapeuta = data.find(terapista => terapista.userId === userAPIData.id);

            if (terapeuta) {                
                setTerapeutaLogada(terapeuta.id);
            } else {
                console.warn("Nenhum terapeuta encontrado com o userId do usuário logado.");
                setTerapeutaLogada(null);
            }
        } catch (error) {
            console.error("Erro ao buscar terapeuta logada:", error);
            setTerapeutaLogada(null);
        }
    };

    const buscarNotificacoes = useCallback(async () => {
        if (!userAPIData || !userAPIData.id) return;
        
        try {
            setLoadingNotificacoes(true);
            const response = await authFetch('notification/user');
            
            if (!response.ok) throw new Error("Erro ao buscar notificações");
            
            const data = await response.json();
            // Filtrar apenas não visualizadas e pegar as últimas 10
            const naoVisualizadas = (data || [])
                .filter(notif => !notif.viewed)
                .slice(0, 10);
            setNotificacoes(naoVisualizadas);
        } catch (error) {
            console.error("Erro ao buscar notificações:", error);
            setNotificacoes([]);
        } finally {
            setLoadingNotificacoes(false);
        }
    }, [userAPIData]);

    useEffect(() => {
    if (userAPIData && userAPIData.id) {
        buscarTerapeutaLogada();
            buscarNotificacoes();
    }
    }, [userAPIData, buscarNotificacoes]);

    // Atualizar notificações periodicamente (a cada 30 segundos)
    useEffect(() => {
        if (!userAPIData || !userAPIData.id) return;
        
        const interval = setInterval(() => {
            buscarNotificacoes();
        }, 30000); // 30 segundos

        return () => clearInterval(interval);
    }, [userAPIData, buscarNotificacoes]);

    const marcarComoVisualizada = async (notificacaoId) => {
        try {
            const response = await authFetch(`notification/${notificacaoId}/viewed`, {
                method: 'PATCH'
            });
            
            if (!response.ok) throw new Error("Erro ao marcar notificação como visualizada");
            
            // Atualizar lista removendo a notificação visualizada
            setNotificacoes(prev => prev.filter(notif => notif.id !== notificacaoId));
        } catch (error) {
            console.error("Erro ao marcar notificação como visualizada:", error);
        }
    };

    const truncarTexto = (texto, maxLength = 80) => {
        if (!texto) return '';
        if (texto.length <= maxLength) return texto;
        return texto.substring(0, maxLength) + '...';
    };

    const formatarData = (dataString) => {
        if (!dataString) return '';
        const data = new Date(dataString);
        const agora = new Date();
        const diffMs = agora - data;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Agora';
        if (diffMins < 60) return `${diffMins} min atrás`;
        if (diffHours < 24) return `${diffHours}h atrás`;
        if (diffDays < 7) return `${diffDays}d atrás`;
        
        return data.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    const notificacoesNaoVisualizadas = notificacoes.filter(notif => !notif.viewed).length;

    useEffect(() => {
        const handleResize = () => {
            const currentIsMobile = window.innerWidth < 992;
            if (currentIsMobile) {
                setIsMobileMenuOpen(false);
            }
        };

        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Fechar menu quando a rota mudar
    useEffect(() => {
        setIsMobileMenuOpen(false);
    }, [location.pathname]);

    const toggleMobileMenu = () => {
        setIsMobileMenuOpen(!isMobileMenuOpen);
    };

    const handleLogout = async () => {
        try {
            await logout();
            navigate('/login');
        } catch (error) {
            // Erro silencioso
        }
    };

    // Se está carregando, mostrar loading
    if (loading) {
        return <PageLoader />;
    }

    // Verificar se é role 6 para renderização condicional
    const isRole6 = userAPIData && (Number(userAPIData.userRoleId) === 6 || Number(userAPIData.userRole?.id) === 6);
    const isAdmin = userAPIData && (Number(userAPIData.userRoleId) === 1 || Number(userAPIData.userRole?.id) === 1);
    const roleId = userAPIData ? Number(userAPIData.userRole?.id || userAPIData.userRoleId) : null;
    const canAccessTriagem = [1, 2, 4].includes(roleId);

    return (
        <div>
            {userAPIData ? (
                <>
                    {/* Overlay para fechar menu no mobile */}
                    {isMobileMenuOpen && (
                        <div 
                            className="navbar-overlay"
                            onClick={() => setIsMobileMenuOpen(false)}
                        />
                    )}
                    {/* Menu Mobile */}
                    <MobileNavbar 
                        userAPIData={userAPIData}
                        isOpen={isMobileMenuOpen}
                        onClose={() => setIsMobileMenuOpen(false)}
                        notificacoes={notificacoes}
                        loadingNotificacoes={loadingNotificacoes}
                        notificacoesNaoVisualizadas={notificacoesNaoVisualizadas}
                        marcarComoVisualizada={marcarComoVisualizada}
                        truncarTexto={truncarTexto}
                        formatarData={formatarData}
                        terapeutaLogada={terapeutaLogada}
                        handleLogout={handleLogout}
                    />
                    {/* Navbar Horizontal */}
                    <nav className="navbar navbar-expand-lg navbar-horizontal fixed-top bg-azul shadow-sm" style={{ zIndex: 1030, paddingTop: '0.5rem', paddingBottom: '0.5rem' }}>
                        <div className="container-fluid">
                            {/* Logo */}
                            <Link to={"/"} className="navbar-brand d-flex align-items-center me-4">
                                <img
                                    src="/logo_inn.png"
                                    alt='Logo Dashboard'
                                    style={{
                                        maxHeight: "50px",
                                        transition: "max-width 0.3s ease"
                                    }}
                                />
                            </Link>

                            {/* Botão hambúrguer para mobile */}
                            <button
                                className="navbar-toggler"
                                type="button"
                                onClick={toggleMobileMenu}
                                aria-controls="navbarNav"
                                aria-expanded={isMobileMenuOpen}
                                aria-label="Toggle navigation"
                                style={{ border: 'none', color: '#fff' }}
                            >
                                <i className={`fa-solid fa-${isMobileMenuOpen ? 'times' : 'bars'}`}></i>
                            </button>

                            {/* Menu de navegação */}
                            <div className="collapse navbar-collapse d-none d-lg-block" id="navbarNav">
                                <ul className="navbar-nav me-auto">
                                    {/* Para role 6, mostrar APENAS o menu de Agendamentos */}
                                    {isRole6 ? (
                                        <li className="nav-item">
                                            <Link to={"/Calendario"} className="nav-link text-white d-flex align-items-center">
                                                <i className="fa-solid fa-calendar-days me-2"></i>
                                                <span>Agendamentos</span>
                                            </Link>
                                        </li>
                                    ) : (
                                        <>
                                            {hasAccess('dashboard') && (
                                                <li className="nav-item">
                                                    <Link to={"/"} className="nav-link text-white d-flex align-items-center" id="dashboard">
                                                        <i className="fa-solid fa-house me-2"></i>
                                                        <span>Dashboard</span>
                                                    </Link>
                                                </li>
                                            )}
                                            {hasAccess('agendamentos') && (
                                                <li className="nav-item">
                                                    <Link to={"/Calendario"} className="nav-link text-white d-flex align-items-center">
                                                        <i className="fa-solid fa-calendar-days me-2"></i>
                                                        <span>Agendamentos</span>
                                                    </Link>
                                                </li>
                                            )}
                                        </>
                                    )}

                                    {canAccessTriagem && hasAccess('pacientes') && (
                                        <li className="nav-item">
                                            <Link className="nav-link text-white d-flex align-items-center" to={"/Triagens"}>
                                                <i className="fa-solid fa-clipboard-list me-2"></i>
                                                <span>Triagem</span>
                                            </Link>
                                        </li>
                                    )}

                                    {!isRole6 && hasAccess('pacientes') && (
                                        <li className="nav-item">
                                            <Link className="nav-link text-white d-flex align-items-center" to={"/Pacientes"}>
                                                <i className="fa-solid fa-children me-2"></i>
                                                <span>Pacientes</span>
                                            </Link>
                                        </li>
                                    )}
                                    {!isRole6 && hasAccess('pacientes') && (
                                        <li className="nav-item">
                                            <Link className="nav-link text-white d-flex align-items-center" to={"/PacientesBeta"}>
                                                <i className="fa-solid fa-list me-2"></i>
                                                <span>Pacientes (beta)</span>
                                            </Link>
                                        </li>
                                    )}

                                    {!isRole6 && ((hasAccess('nosso-time') || hasAccess('pacientes') || hasAccess('responsaveis') || hasAccess('usuarios') || hasAccess('prestadores-servico'))) && (
                                        <li className="nav-item dropdown">
                                            <a
                                                className="nav-link text-white d-flex align-items-center dropdown-toggle"
                                                href="#"
                                                id="dropdownCadastros"
                                                role="button"
                                                data-bs-toggle="dropdown"
                                                aria-expanded="false"
                                            >
                                                <i className="fa-solid fa-folder-open me-2"></i>
                                                <span>Cadastros</span>
                                            </a>
                                            <ul className="dropdown-menu" aria-labelledby="dropdownCadastros">
                                                {hasAccess('nosso-time') && (
                                                    <li>
                                                        <Link className="dropdown-item" to={'/Terapeutas'}>
                                                            <i className="fa-solid fa-people-group me-2"></i>
                                                            Nosso Time
                                                        </Link>
                                                    </li>
                                                )}
                                                {/* {hasAccess('responsaveis') && (
                                                    <li>
                                                        <Link className="dropdown-item" to={"/Responsaveis"}>
                                                            <i className="fa-solid fa-person me-2"></i>
                                                            Responsaveis
                                                        </Link>
                                                    </li>
                                                )} */}
                                                {hasAccess('prestadores-servico') && (
                                                    <li>
                                                        <Link className="dropdown-item" to={"/PrestServicos"}>
                                                            <i className="fa-solid fa-screwdriver-wrench me-2"></i>
                                                            Prestadores de Serviço
                                                        </Link>
                                                    </li>
                                                )}
                                                {userAPIData && hasAccess('usuarios') && (
                                                    <li>
                                                        <Link className="dropdown-item" to={"/Usuarios"}>
                                                            <i className="fa-solid fa-user-gear me-2"></i>
                                                            Usuários
                                                        </Link>
                                                    </li>
                                                )}
                                            </ul>
                                        </li>
                                    )}

                                    {/* Menu Gestão - não mostrar para role 6 (somente leitura) */}
                                    {userAPIData && Number(userAPIData.userRoleId) !== 6 && Number(userAPIData.userRole?.id) !== 6 && (hasAccess('exportacoes') || hasAccess('calendario-gerencial') || hasAccess('agendamentos')) && (
                                        <li className="nav-item dropdown">
                                            <a
                                                className="nav-link text-white d-flex align-items-center dropdown-toggle"
                                                href="#"
                                                id="dropdownGestao"
                                                role="button"
                                                data-bs-toggle="dropdown"
                                                aria-expanded="false"
                                            >
                                                <i className="fa-solid fa-chart-line me-2"></i>
                                                <span>Gestão</span>
                                            </a>
                                            <ul className="dropdown-menu" aria-labelledby="dropdownGestao">
                                                {hasAccess('exportacoes') && (
                                                    <li>
                                                        <Link className="dropdown-item" to={"/Exportacoes"}>
                                                            <i className="fa-solid fa-file-arrow-down me-2"></i>
                                                            Exportações
                                                        </Link>
                                                    </li>
                                                )}
                                                {hasAccess('calendario-gerencial') && (
                                                    <li>
                                                        <Link className="dropdown-item" to={"/Calendario/Gerenciamento"}>
                                                            <i className="fa-regular fa-calendar-plus me-2"></i>
                                                            Calendário
                                                        </Link>
                                                    </li>
                                                )}
                                                {hasAccess('agendamentos') && (
                                                    <li>
                                                        <Link className="dropdown-item" to={"/Calendario/Reposicoes"}>
                                                            <i className="fa-solid fa-calendar-check me-2"></i>
                                                            Reposições
                                                        </Link>
                                                    </li>
                                                )}
                                                {isAdmin && (
                                                <li>
                                                    <Link className="dropdown-item" to={"/Terapeutas/Escala"}>
                                                        <i className="fa-regular fa-calendar-days me-2"></i>
                                                        Escala de Trabalho
                                                    </Link>
                                                </li>
                                                )}
                                            </ul>
                                        </li>
                                    )}

                                    {!isRole6 && userAPIData && hasAccess('avisos') && (
                                        <li className="nav-item">
                                            <Link className="nav-link text-white d-flex align-items-center" to={"/Avisos"}>
                                                <i className="fa-solid fa-triangle-exclamation me-2"></i>
                                                <span>Avisos</span>
                                            </Link>
                                        </li>
                                    )}
                                </ul>

                                {/* Notificações e Perfil - à direita */}
                                <ul className="navbar-nav ms-auto d-flex flex-row align-items-center">
                                    <li className="nav-item dropdown pe-4 d-flex align-items-center">
                                        <Link className="nav-link text-white p-0 position-relative d-flex align-items-center" id="dropdownMenuButtonNotifications" data-bs-toggle="dropdown" aria-expanded="false" style={{ height: '100%' }}>
                                            <i className="fa fa-bell cursor-pointer" style={{ fontSize: '1.2rem', lineHeight: '1' }}></i>
                                            {notificacoesNaoVisualizadas > 0 && (
                                                <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger" style={{ fontSize: '0.65rem', minWidth: '18px', height: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    {notificacoesNaoVisualizadas > 9 ? '9+' : notificacoesNaoVisualizadas}
                                                </span>
                                            )}
                                        </Link>
                                    <ul className="dropdown-menu dropdown-menu-end px-2 py-3 me-sm-n4" style={{ maxWidth: '380px', maxHeight: '400px', overflowY: 'auto' }} aria-labelledby="dropdownMenuButtonNotifications">
                                        {loadingNotificacoes ? (
                                            <li className="mb-2 text-center">
                                                <div className="spinner-border spinner-border-sm text-primary" role="status">
                                                    <span className="visually-hidden">Carregando...</span>
                                                </div>
                                            </li>
                                        ) : notificacoes.length === 0 ? (
                                            <li className="mb-2">
                                                <div className="dropdown-item border-radius-md text-center text-muted">
                                                    <p className="text-xs mb-0">Nenhuma notificação nova</p>
                                                </div>
                                            </li>
                                        ) : (
                                            <>
                                                <li className="mb-2">
                                                    <div className="dropdown-item border-radius-md pb-1">
                                                        <div className="row g-0">
                                                            <div className="col-10">
                                                                <span className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Notificação</span>
                                                            </div>
                                                            <div className="col-2 text-end">
                                                                <span className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Visto</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </li>
                                                {notificacoes.map((notificacao) => (
                                                    <li key={notificacao.id} className="mb-2">
                                                        <div className="dropdown-item border-radius-md">
                                                            <div className="row g-0">
                                                                <div className="col-10 pe-2" style={{ minWidth: 0, maxWidth: '83.333333%', overflow: 'hidden' }}>
                                                                    <div className="d-flex flex-column justify-content-center" style={{ width: '100%' }}>
                                                                        <h6 className="text-sm font-weight-normal mb-1" style={{ wordBreak: 'break-word', overflowWrap: 'break-word', maxWidth: '100%', overflow: 'hidden' }}>
                                                                            <span className="font-weight-bold" style={{ display: 'block', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>{truncarTexto(notificacao.description || 'Sem descrição')}</span>
                                                                        </h6>
                                                                        <p className="text-xs text-secondary mb-0" style={{ maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                            <i className="fa fa-clock me-1"></i>
                                                                            {formatarData(notificacao.createdAt || notificacao.createdDate)}
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                                <div className="col-2 d-flex align-items-center justify-content-center" style={{ minWidth: '40px', flexShrink: 0, maxWidth: '16.666667%' }}>
                                                                    <div className="form-check">
                                                                        <input
                                                                            className="form-check-input"
                                                                            type="checkbox"
                                                                            checked={notificacao.viewed || false}
                                                                            onChange={() => marcarComoVisualizada(notificacao.id)}
                                                                            id={`notif-${notificacao.id}`}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </li>
                                                ))}
                                                <li className="mb-2">
                                                    <hr className="dropdown-divider" />
                                                </li>
                                                <li className="mb-2">
                                                    <Link 
                                                        className="dropdown-item border-radius-md text-center text-primary" 
                                                        to={`/Avisos?userId=${userAPIData?.id}`}
                                                        onClick={() => {
                                                            const dropdown = document.getElementById('dropdownMenuButtonNotifications');
                                                            if (dropdown) {
                                                                const bsDropdown = window.bootstrap?.Dropdown?.getInstance(dropdown);
                                                                if (bsDropdown) bsDropdown.hide();
                                                            }
                                                        }}
                                                    >
                                                        <span className="font-weight-bold">Ver todas</span>
                                                    </Link>
                                                </li>
                                            </>
                                        )}
                                    </ul>
                                </li>
                                <li className="nav-item dropdown pe-2 d-flex align-items-center">
                                    <Link className="nav-link text-white p-0 d-flex align-items-center" id="dropdownMenuButton" data-bs-toggle="dropdown" aria-expanded="false" to={"/"} style={{ height: '100%' }}>
                                        <i className="fa fa-user me-sm-1" style={{ fontSize: '1.2rem', lineHeight: '1' }}></i>
                                        <span className="d-sm-inline d-none ms-2">
                                            {userAPIData && userAPIData.name}
                                        </span>
                                    </Link>
                                    <ul className="dropdown-menu dropdown-menu-end px-2 py-3 me-sm-n4" style={{ minWidth: '200px' }} aria-labelledby="dropdownMenuButton">
                                        {userAPIData && (
                                            <li className="mb-2">
                                                <div className="dropdown-item border-radius-md pb-2">
                                                    <div className="d-flex flex-column">
                                                        <h6 className="text-sm font-weight-bold mb-0" style={{ color: '#344767' }}>
                                                            {userAPIData.name}
                                                        </h6>
                                                        {userAPIData.email && (
                                                            <p className="text-xs text-secondary mb-0 mt-1">
                                                                {userAPIData.email}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            </li>
                                        )}
                                        {userAPIData && (
                                            <li className="mb-2">
                                                <hr className="dropdown-divider my-1" />
                                            </li>
                                        )}
                                        {userAPIData && (
                                            <li className="mb-2">
                                                <Link 
                                                    className="dropdown-item border-radius-md" 
                                                    to={`/Usuarios/Editar/${userAPIData.id}`}
                                                    onClick={() => {
                                                        const dropdown = document.getElementById('dropdownMenuButton');
                                                        if (dropdown) {
                                                            const bsDropdown = window.bootstrap?.Dropdown?.getInstance(dropdown);
                                                            if (bsDropdown) bsDropdown.hide();
                                                        }
                                                    }}
                                                >
                                                    <div className="d-flex align-items-center py-1">
                                                        <i className="fa fa-user-circle me-2 text-primary"></i>
                                                        <span className="text-sm">Perfil</span>
                                                    </div>
                                                </Link>
                                            </li>
                                        )}
                                        {terapeutaLogada !== null && (
                                            <li className="mb-2">
                                                <Link
                                                    className="dropdown-item border-radius-md"
                                                    to={`/Terapeutas/PerfilTerapeuta/${terapeutaLogada}`}
                                                    state={{ abrirEscala: true }}
                                                    onClick={() => {
                                                        const dropdown = document.getElementById('dropdownMenuButton');
                                                        if (dropdown) {
                                                            const bsDropdown = window.bootstrap?.Dropdown?.getInstance(dropdown);
                                                            if (bsDropdown) bsDropdown.hide();
                                                        }
                                                    }}
                                                >
                                                    <div className="d-flex align-items-center py-1">
                                                        <i className="fa fa-calendar-alt me-2 text-info"></i>
                                                        <span className="text-sm">Minha Escala</span>
                                                    </div>
                                                </Link>
                                            </li>
                                        )}
                                        {!isRole6 && (
                                            <li className="mb-2">
                                                <Link
                                                    className="dropdown-item border-radius-md"
                                                    to="/Calendario/Disponibilidade"
                                                    onClick={() => {
                                                        const dropdown = document.getElementById('dropdownMenuButton');
                                                        if (dropdown) {
                                                            const bsDropdown = window.bootstrap?.Dropdown?.getInstance(dropdown);
                                                            if (bsDropdown) bsDropdown.hide();
                                                        }
                                                    }}
                                                >
                                                    <div className="d-flex align-items-center py-1">
                                                        <i className="fa fa-clock me-2 text-success"></i>
                                                        <span className="text-sm">Disponibilidade</span>
                                                    </div>
                                                </Link>
                                            </li>
                                        )}
                                        <li className="mb-2">
                                            <hr className="dropdown-divider my-1" />
                                        </li>
                                        <li className="mb-0">
                                            <a 
                                                className="dropdown-item border-radius-md" 
                                                onClick={handleLogout}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <div className="d-flex align-items-center py-1">
                                                    <i className="fa fa-sign-out-alt me-2 text-danger"></i>
                                                    <span className="text-sm">Sair</span>
                                                </div>
                                            </a>
                                        </li>
                                    </ul>
                                </li>
                            </ul>
                            </div>
                        </div>
                    </nav>

                    {/* Conteúdo principal com padding-top para compensar navbar fixo */}
                    <main className="main-content-horizontal">
                        <div className="container-fluid">
                            <Notification />
                            <Outlet />
                        </div>
                    </main>
                </>
            ) : (
                <PageLoader />
            )}
        </div>
    )
}

export default Navbar